import { Button, Col, Form, Input, Row, Select, Typography } from 'antd'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Detergent } from '@/types'
import { CreateWashingMethodRequest } from '@/types/apis/washingMethods/CreateWashingMethodRequest'

import { SelectWrapper } from '../../Customer/styles'
import { SpaceCustom } from '../../style'

type Props = {
  loading: boolean
  onSubmit: (formValues: CreateWashingMethodRequest) => void
  listDetergent: Detergent[] | undefined
}

type WashingMethodFormValues = {
  methodName: string
  time: number
  code: number
  volume: number
  cost: number
  waterName?: string
  quantity?: number
  waterQuantity: number
  detergents: {
    detergentName: string | undefined
    quantity: number | undefined
  }[]
}

type VolumeOptions = {
  value: number
  label: string
}

const { Title } = Typography

const CreateWashingMethodForm = ({ loading, onSubmit, listDetergent }: Props) => {
  const { t } = useTranslation()

  const [form] = Form.useForm<WashingMethodFormValues>()
  const volumeOptions: VolumeOptions[] = [
    { value: 100, label: '100' },
    { value: 75, label: '75' },
    { value: 50, label: '50' },
  ]

  const handleSubmit = (values: WashingMethodFormValues) => {
    const formattedPayload: CreateWashingMethodRequest = {
      name: values.methodName,
      time: Number(values.time),
      volume: Number(values.volume),
      code: Number(values.code),
      cost: Number(values.cost),
      waterQuantity: Number(values.waterQuantity),
      detergents: values.detergents.map((d) => ({
        detergentId: listDetergent?.find((det) => det.name === d.detergentName)?.id || 0,
        quantity: Number(d.quantity) || 0,
      })),
    }

    onSubmit(formattedPayload)
  }

  useEffect(() => {
    if (listDetergent) {
      const formattedDetergents = listDetergent
        .filter((detergent) => !detergent.isWater)
        .map((detergent) => ({
          detergentName: detergent.name,
          quantity: 0,
        }))

      form.setFieldsValue({
        detergents: formattedDetergents,
      })
    }
  }, [form, listDetergent])

  return (
    <Form layout="vertical" onFinish={handleSubmit} form={form}>
      <Title level={5}>{t('components.pages.washingMethods.create.form.method')}</Title>

      <Form.Item
        label={t('components.pages.washingMethods.create.form.no')}
        name="code"
        rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireNo') }]}
      >
        <Input type="number" min={0} />
      </Form.Item>

      <Form.Item
        label={t('components.pages.washingMethods.create.form.name')}
        name="methodName"
        rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireName') }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label={t('components.pages.washingMethods.create.form.time')}
        name="time"
        rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireTime') }]}
      >
        <Input type="number" min={0} suffix="分" />
      </Form.Item>

      <SelectWrapper>
        <Form.Item
          label={t('components.pages.washingMethods.create.form.volume')}
          name="volume"
          rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireVolume') }]}
        >
          <Select>
            {volumeOptions.map((volume) => (
              <Select.Option key={volume.value} value={volume.value}>
                {volume.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </SelectWrapper>

      <Form.Item
        label={t('components.pages.washingMethods.create.form.cost')}
        name="cost"
        rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireCost') }]}
      >
        <Input type="number" min={0} suffix="円" />
      </Form.Item>

      <Form.Item
        label={t('components.pages.washingMethods.create.form.waterName')}
        name="waterQuantity"
        rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireQuantity') }]}
      >
        <Input type="number" min={0} suffix="l" />
      </Form.Item>

      <Title level={5}>{t('components.pages.washingMethods.create.form.detergentUsed')}</Title>
      <Form.List name="detergents">
        {(fields) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              return (
                <Row key={key} gutter={16} align="middle">
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      label={t('components.pages.washingMethods.update.form.detergentName')}
                      name={[name, 'detergentName']}
                      rules={[
                        {
                          required: true,
                          message: t('components.pages.washingMethods.update.form.requireDetergentName'),
                        },
                      ]}
                    >
                      <Input disabled={true} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      label={t('components.pages.washingMethods.update.form.quantity')}
                      name={[name, 'quantity']}
                      rules={[
                        { required: true, message: t('components.pages.washingMethods.update.form.requireQuantity') },
                      ]}
                    >
                      <Input type="number" min={0} suffix="ml" />
                    </Form.Item>
                  </Col>
                </Row>
              )
            })}
          </>
        )}
      </Form.List>
      <SpaceCustom>
        <Button type="primary" htmlType="submit" loading={loading}>
          {t('components.pages.washingMethods.create.form.addNew')}
        </Button>
      </SpaceCustom>
    </Form>
  )
}

CreateWashingMethodForm.displayName = 'CreateWashingMethodForm'
export default CreateWashingMethodForm
